import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface FirstInterviewFormProps {
  candidateId: string;
  candidateName?: string;
  interviewId?: string;
  onComplete: (shouldScheduleSecond?: boolean) => void;
  onCancel?: () => void;
}

export function FirstInterviewForm({ candidateId, candidateName, interviewId, onComplete, onCancel }: FirstInterviewFormProps) {
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft('first_interview', candidateId);
  
  const [formData, setFormData] = useState(() => {
    // Load from secure database draft
    if (draft) {
      return draft;
    }
    
    // Default values
    return {
      // Basic Info
      candidateName: candidateName,
      interviewer: "",
      date: new Date().toISOString().split('T')[0],
      
      // Ministerial Decision Fields
      ministerialDecision: "",
      
      // Missing fields from the form image
      currentAddress: "",
      permanentAddress: "",
      phoneNumber: "",
      email: "",
      birthPlace: "",
      maritalStatus: "",
      children: "",
      fatherName: "",
      motherName: "",
      idNumber: "",
      
      // Experience Questions
      hasSecurityExperience: "",
      securityExperienceDetails: "",
      hasGuardingExperience: "",
      guardingExperienceDetails: "",
      hasWeaponsExperience: "",
      weaponsExperienceDetails: "",
      
      // Background Questions
      hasSecurityEducation: "",
      securityEducationDetails: "",
      hasArmyService: "",
      armyServiceDetails: "",
      hasPoliceService: "",
      policeServiceDetails: "",
      
      // Equipment Questions
      hasEquipmentExperience: "",
      equipmentExperienceDetails: "",
      hasSecuritySystemsExperience: "",
      securitySystemsDetails: "",
      
      // Transportation Questions
      hasDriversLicense: "",
      licenseType: "",
      hasOwnVehicle: "",
      vehicleDetails: "",
      
      // Availability Questions
      canWorkNights: "",
      canWorkWeekends: "",
      canWorkHolidays: "",
      availabilityNotes: "",
      
      // Additional Questions from Interview Form
      academicQualifications: "",
      professionalCertifications: "",
      personalReferences: "",
      emergencyContactInfo: "",
    
    // Yes/No Questions from form
    canStartImmediately: "",
    hasHealthIssues: "",
    hasCriminalRecord: "",
    willingToRelocate: "",
    hasWorkingContract: "",
    hasValidWorkPermit: "",
    agreedToBackgroundCheck: "",
    
    // Assessment Fields
    generalNotes: "",
    strengths: "",
    areasForImprovement: "",
      interviewerRecommendation: "pending"
    };
  });

  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Save to database whenever formData changes (debounced via hook)
  useEffect(() => {
    const hasData = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== "" && value !== "pending"
    );
    if (hasData) {
      saveDraft(formData);
    }
  }, [formData]);

  // Prevent accidental navigation/refresh when there's unsaved data
  useEffect(() => {
    const hasUnsavedData = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== "" && value !== "pending"
    );

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = "Έχετε μη αποθηκευμένα δεδομένα. Είστε σίγουροι ότι θέλετε να φύγετε;";
        return e.returnValue;
      }
    };

    if (hasUnsavedData) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData]);

  // Load existing interview data when editing
  useEffect(() => {
    const loadInterviewData = async () => {
      if (interviewId) {
        try {
          const { data: interview, error } = await supabase
            .from('interviews')
            .select('*')
            .eq('id', interviewId)
            .single();

          if (error) throw error;

          if (interview?.interview_data && typeof interview.interview_data === 'object') {
            setFormData(prev => ({
              ...prev,
              ...(interview.interview_data as Record<string, any>),
              date: interview.scheduled_date ? 
                new Date(interview.scheduled_date).toISOString().split('T')[0] : 
                prev.date
            }));
          }
        } catch (error: any) {
          console.error('Error loading interview data:', error);
          toast({
            title: "Σφάλμα",
            description: "Αποτυχία φόρτωσης δεδομένων συνέντευξης",
            variant: "destructive",
          });
        }
      }
    };

    loadInterviewData();
  }, [interviewId, toast]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (interviewId) {
        // Update existing interview
        const { error: interviewError } = await supabase
          .from('interviews')
          .update({
            interview_data: formData,
            completed_at: new Date().toISOString(),
            recommendation: formData.interviewerRecommendation === 'pending' ? null : formData.interviewerRecommendation,
            status: 'completed'
          })
          .eq('id', interviewId);

        if (interviewError) throw interviewError;

        toast({
          title: "Επιτυχία",
          description: "Η πρώτη συνέντευξη ενημερώθηκε επιτυχώς",
        });
      } else {
        // Create new interview record
        const { data: interview, error: interviewError } = await supabase
          .from('interviews')
          .insert([{
            candidate_id: candidateId,
            interview_type: 'first',
            status: 'completed',
            scheduled_date: new Date().toISOString(),
            interview_data: formData,
            completed_at: new Date().toISOString(),
            recommendation: formData.interviewerRecommendation
          }])
          .select()
          .single();

        if (interviewError) throw interviewError;

        // Update candidate status
        const { error: updateError } = await supabase
          .from('candidates')
          .update({ status: 'first_interview_completed' })
          .eq('id', candidateId);

        if (updateError) throw updateError;

        toast({
          title: "Επιτυχία",
          description: "Η πρώτη συνέντευξη ολοκληρώθηκε και αποθηκεύτηκε",
        });
      }

      // Clear saved data after successful submission
      await deleteDraft();

      // Check if candidate should be promoted to second interview
      const shouldScheduleSecond = formData.interviewerRecommendation === 'promote_to_second';
      onComplete(shouldScheduleSecond);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Αποτυχία αποθήκευσης συνέντευξης",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>
          {interviewId ? 'Επεξεργασία' : 'Καταγραφή'} - Πρώτη Συνέντευξη - {candidateName}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="interviewer">Όνομα Συνεντευκτή</Label>
              <Input
                id="interviewer"
                value={formData.interviewer}
                onChange={(e) => handleInputChange('interviewer', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="date">Ημερομηνία Συνέντευξης</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                required
              />
            </div>
          </div>


          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Προσωπικά Στοιχεία</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="currentAddress">Διεύθυνση Κατοικίας</Label>
                <Input
                  id="currentAddress"
                  value={formData.currentAddress}
                  onChange={(e) => handleInputChange('currentAddress', e.target.value)}
                  placeholder="Τρέχουσα διεύθυνση κατοικίας"
                />
              </div>
              <div>
                <Label htmlFor="permanentAddress">Μόνιμη Διεύθυνση</Label>
                <Input
                  id="permanentAddress"
                  value={formData.permanentAddress}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value)}
                  placeholder="Μόνιμη διεύθυνση"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phoneNumber">Τηλέφωνο</Label>
                <Input
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Τηλέφωνο επικοινωνίας"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Διεύθυνση email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="birthPlace">Τόπος Γέννησης</Label>
                <Input
                  id="birthPlace"
                  value={formData.birthPlace}
                  onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                  placeholder="Τόπος γέννησης"
                />
              </div>
              <div>
                <Label htmlFor="maritalStatus">Οικογενειακή Κατάσταση</Label>
                <RadioGroup
                  value={formData.maritalStatus}
                  onValueChange={(value) => handleInputChange('maritalStatus', value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="single" id="single" />
                    <Label htmlFor="single">Άγαμος/η</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="married" id="married" />
                    <Label htmlFor="married">Έγγαμος/η</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="divorced" id="divorced" />
                    <Label htmlFor="divorced">Διαζευγμένος/η</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="widowed" id="widowed" />
                    <Label htmlFor="widowed">Χήρος/α</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="children">Παιδιά</Label>
                <Input
                  id="children"
                  value={formData.children}
                  onChange={(e) => handleInputChange('children', e.target.value)}
                  placeholder="Αριθμός παιδιών"
                />
              </div>
              <div>
                <Label htmlFor="fatherName">Όνομα Πατρός</Label>
                <Input
                  id="fatherName"
                  value={formData.fatherName}
                  onChange={(e) => handleInputChange('fatherName', e.target.value)}
                  placeholder="Όνομα πατέρα"
                />
              </div>
              <div>
                <Label htmlFor="motherName">Όνομα Μητρός</Label>
                <Input
                  id="motherName"
                  value={formData.motherName}
                  onChange={(e) => handleInputChange('motherName', e.target.value)}
                  placeholder="Όνομα μητέρας"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="idNumber">Αριθμός Ταυτότητας</Label>
              <Input
                id="idNumber"
                value={formData.idNumber}
                onChange={(e) => handleInputChange('idNumber', e.target.value)}
                placeholder="Αριθμός ταυτότητας"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="academicQualifications">Επίπεδο Εκπαίδευσης</Label>
                <Textarea
                  id="academicQualifications"
                  value={formData.academicQualifications}
                  onChange={(e) => handleInputChange('academicQualifications', e.target.value)}
                  placeholder="Σπουδές, πτυχία, πιστοποιήσεις..."
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="professionalCertifications">Επαγγελματικές Πιστοποιήσεις</Label>
                <Textarea
                  id="professionalCertifications"
                  value={formData.professionalCertifications}
                  onChange={(e) => handleInputChange('professionalCertifications', e.target.value)}
                  placeholder="Επαγγελματικές πιστοποιήσεις και άδειες..."
                  rows={2}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="personalReferences">Συστάσεις</Label>
              <Textarea
                id="personalReferences"
                value={formData.personalReferences}
                onChange={(e) => handleInputChange('personalReferences', e.target.value)}
                placeholder="Στοιχεία προσώπων που μπορούν να σας συστήσουν..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="emergencyContactInfo">Στοιχεία Επείγουσας Ανάγκης</Label>
              <Textarea
                id="emergencyContactInfo"
                value={formData.emergencyContactInfo}
                onChange={(e) => handleInputChange('emergencyContactInfo', e.target.value)}
                placeholder="Άτομο επικοινωνίας σε περίπτωση ανάγκης..."
                rows={2}
              />
            </div>
          </div>

          {/* Experience Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Επαγγελματική Εμπειρία</h3>
            
            <div className="space-y-3">
              <Label>Έχετε εμπειρία στην ασφάλεια;</Label>
              <RadioGroup
                value={formData.hasSecurityExperience}
                onValueChange={(value) => handleInputChange('hasSecurityExperience', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="sec-exp-yes" />
                  <Label htmlFor="sec-exp-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="sec-exp-no" />
                  <Label htmlFor="sec-exp-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasSecurityExperience === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε την εμπειρία σας στην ασφάλεια..."
                  value={formData.securityExperienceDetails}
                  onChange={(e) => handleInputChange('securityExperienceDetails', e.target.value)}
                />
              )}
            </div>

            <div className="space-y-3">
              <Label>Έχετε εμπειρία σε φύλαξη;</Label>
              <RadioGroup
                value={formData.hasGuardingExperience}
                onValueChange={(value) => handleInputChange('hasGuardingExperience', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="guard-exp-yes" />
                  <Label htmlFor="guard-exp-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="guard-exp-no" />
                  <Label htmlFor="guard-exp-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasGuardingExperience === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε την εμπειρία σας στη φύλαξη..."
                  value={formData.guardingExperienceDetails}
                  onChange={(e) => handleInputChange('guardingExperienceDetails', e.target.value)}
                />
              )}
            </div>

            <div className="space-y-3">
              <Label>Έχετε εμπειρία με όπλα;</Label>
              <RadioGroup
                value={formData.hasWeaponsExperience}
                onValueChange={(value) => handleInputChange('hasWeaponsExperience', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="weapons-exp-yes" />
                  <Label htmlFor="weapons-exp-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="weapons-exp-no" />
                  <Label htmlFor="weapons-exp-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasWeaponsExperience === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε την εμπειρία σας με όπλα..."
                  value={formData.weaponsExperienceDetails}
                  onChange={(e) => handleInputChange('weaponsExperienceDetails', e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Background Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Εκπαίδευση & Υπηρεσίες</h3>
            
            <div className="space-y-3">
              <Label>Έχετε εκπαίδευση στην ασφάλεια;</Label>
              <RadioGroup
                value={formData.hasSecurityEducation}
                onValueChange={(value) => handleInputChange('hasSecurityEducation', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="sec-edu-yes" />
                  <Label htmlFor="sec-edu-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="sec-edu-no" />
                  <Label htmlFor="sec-edu-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasSecurityEducation === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε την εκπαίδευσή σας στην ασφάλεια..."
                  value={formData.securityEducationDetails}
                  onChange={(e) => handleInputChange('securityEducationDetails', e.target.value)}
                />
              )}
            </div>

            <div className="space-y-3">
              <Label>Έχετε στρατιωτική θητεία;</Label>
              <RadioGroup
                value={formData.hasArmyService}
                onValueChange={(value) => handleInputChange('hasArmyService', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="army-yes" />
                  <Label htmlFor="army-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="army-no" />
                  <Label htmlFor="army-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasArmyService === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε τη στρατιωτική σας θητεία..."
                  value={formData.armyServiceDetails}
                  onChange={(e) => handleInputChange('armyServiceDetails', e.target.value)}
                />
              )}
            </div>

            <div className="space-y-3">
              <Label>Έχετε υπηρετήσει στην Αστυνομία;</Label>
              <RadioGroup
                value={formData.hasPoliceService}
                onValueChange={(value) => handleInputChange('hasPoliceService', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="police-yes" />
                  <Label htmlFor="police-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="police-no" />
                  <Label htmlFor="police-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasPoliceService === 'yes' && (
                <Textarea
                  placeholder="Περιγράψτε την υπηρεσία σας στην Αστυνομία..."
                  value={formData.policeServiceDetails}
                  onChange={(e) => handleInputChange('policeServiceDetails', e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Transportation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Μεταφορά</h3>
            
            <div className="space-y-3">
              <Label>Έχετε έγκυρη άδεια οδήγησης;</Label>
              <RadioGroup
                value={formData.hasDriversLicense}
                onValueChange={(value) => handleInputChange('hasDriversLicense', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="license-yes" />
                  <Label htmlFor="license-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="license-no" />
                  <Label htmlFor="license-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasDriversLicense === 'yes' && (
                <Input
                  placeholder="Κατηγορία άδειας (Α, Β, Γ, κλπ.)"
                  value={formData.licenseType}
                  onChange={(e) => handleInputChange('licenseType', e.target.value)}
                />
              )}
            </div>

            <div className="space-y-3">
              <Label>Έχετε δικό σας όχημα;</Label>
              <RadioGroup
                value={formData.hasOwnVehicle}
                onValueChange={(value) => handleInputChange('hasOwnVehicle', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="vehicle-yes" />
                  <Label htmlFor="vehicle-yes">Ναι</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="vehicle-no" />
                  <Label htmlFor="vehicle-no">Όχι</Label>
                </div>
              </RadioGroup>
              {formData.hasOwnVehicle === 'yes' && (
                <Textarea
                  placeholder="Στοιχεία οχήματος (μάρκα, μοντέλο, έτος)"
                  value={formData.vehicleDetails}
                  onChange={(e) => handleInputChange('vehicleDetails', e.target.value)}
                />
              )}
            </div>
          </div>

          {/* Availability Questions */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Διαθεσιμότητα Εργασίας</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label>Μπορείτε να εργαστείτε σε νυχτερινά ωράρια;</Label>
                <RadioGroup
                  value={formData.canWorkNights}
                  onValueChange={(value) => handleInputChange('canWorkNights', value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="nights-yes" />
                    <Label htmlFor="nights-yes">Ναι</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="nights-no" />
                    <Label htmlFor="nights-no">Όχι</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Μπορείτε να εργαστείτε Σαββατοκύριακα;</Label>
                <RadioGroup
                  value={formData.canWorkWeekends}
                  onValueChange={(value) => handleInputChange('canWorkWeekends', value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="weekends-yes" />
                    <Label htmlFor="weekends-yes">Ναι</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="weekends-no" />
                    <Label htmlFor="weekends-no">Όχι</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Μπορείτε να εργαστείτε σε αργίες;</Label>
                <RadioGroup
                  value={formData.canWorkHolidays}
                  onValueChange={(value) => handleInputChange('canWorkHolidays', value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="holidays-yes" />
                    <Label htmlFor="holidays-yes">Ναι</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="holidays-no" />
                    <Label htmlFor="holidays-no">Όχι</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Μπορείτε να ξεκινήσετε άμεσα;</Label>
                <RadioGroup
                  value={formData.canStartImmediately}
                  onValueChange={(value) => handleInputChange('canStartImmediately', value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="start-yes" />
                    <Label htmlFor="start-yes">Ναι</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="start-no" />
                    <Label htmlFor="start-no">Όχι</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div>
              <Label htmlFor="availabilityNotes">Επιπλέον Σημειώσεις Διαθεσιμότητας</Label>
              <Textarea
                id="availabilityNotes"
                value={formData.availabilityNotes}
                onChange={(e) => handleInputChange('availabilityNotes', e.target.value)}
                placeholder="Ειδικές απαιτήσεις ή περιορισμοί..."
                rows={2}
              />
            </div>
          </div>

          {/* Assessment */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Αξιολόγηση Συνέντευξης</h3>
            
            <div>
              <Label htmlFor="generalNotes">Γενικές Παρατηρήσεις</Label>
              <Textarea
                id="generalNotes"
                value={formData.generalNotes}
                onChange={(e) => handleInputChange('generalNotes', e.target.value)}
                placeholder="Γενικές παρατηρήσεις και σημειώσεις..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="strengths">Δυνατά Σημεία Υποψηφίου</Label>
              <Textarea
                id="strengths"
                value={formData.strengths}
                onChange={(e) => handleInputChange('strengths', e.target.value)}
                placeholder="Κύρια δυνατά σημεία που παρατηρήθηκαν..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="areasForImprovement">Περιοχές Βελτίωσης</Label>
              <Textarea
                id="areasForImprovement"
                value={formData.areasForImprovement}
                onChange={(e) => handleInputChange('areasForImprovement', e.target.value)}
                placeholder="Περιοχές που χρειάζονται ανάπτυξη..."
                rows={2}
              />
            </div>

            <div>
              <Label>Σύσταση Συνεντευκτή</Label>
              <RadioGroup
                value={formData.interviewerRecommendation}
                onValueChange={(value) => handleInputChange('interviewerRecommendation', value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="proceed" id="rec-proceed" />
                  <Label htmlFor="rec-proceed">Συνέχεια στη Δεύτερη Συνέντευξη</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reject" id="rec-reject" />
                  <Label htmlFor="rec-reject">Απόρριψη</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pending" id="rec-pending" />
                  <Label htmlFor="rec-pending">Χρειάζεται Περαιτέρω Εξέταση</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            {onCancel && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  const hasUnsavedData = Object.values(formData).some(value => 
                    Array.isArray(value) ? value.length > 0 : value !== "" && value !== "pending"
                  );
                  
                  if (hasUnsavedData) {
                    if (window.confirm("Έχετε μη αποθηκευμένα δεδομένα. Είστε σίγουροι ότι θέλετε να ακυρώσετε;")) {
                      deleteDraft();
                      onCancel();
                    }
                  } else {
                    onCancel();
                  }
                }}
              >
                Ακύρωση
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? "Αποθήκευση..." : interviewId ? "Ενημέρωση Συνέντευξης" : "Ολοκλήρωση Συνέντευξης"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}